<script setup>
import { nextTick, ref, onMounted, onUnmounted, computed } from 'vue'
import { NOTE_MAX_LENGTH } from '@/utils/profileNotes'
import { useRestaurantNotes } from '@/composables/useRestaurantNotes'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useRestaurantsStore, useUsersStore, useUserGroupsStore, useReservationsStore } from '@/stores'
import moment from 'moment'
import FriendsLikedChips from '@core/components/FriendsLikedChips.vue'
import RestaurantTitleBlock from '@core/components/RestaurantTitleBlock.vue'
import CmsText from '@/@core/components/CmsText.vue'
import GooglePlacesService from '@/services/GooglePlacesService'

const props = defineProps({
  item: {
    type: Object,
    required: true
  },

  // Index in the list - used to determine if image is above the fold
  index: {
    type: Number,
    default: 0
  },

  // Friends who favorited, passed directly (e.g. from friends-favorites endpoint)
  // The network line's label: RM AI's Discovery cards pass "Favorited by you/your network" (10/20).
  favoritedByLabel: {
    type: String,
    default: 'Favorited by',
  },
  friendsWhoFavoritedProp: {
    type: Array,
    default: null
  },

  // True when the logged-in member has a Preferred grant at this restaurant
  memberPreferred: {
    type: Boolean,
    default: false
  },

  // Hide the city line (e.g. when the browser has a specific city selected
  // and every card would repeat it)
  hideMarket: {
    type: Boolean,
    default: false
  },

  // Show the member's note directly on the card (Favorites, matching
  // Bookings) instead of tucking it behind the expand chevron
  inlineNotes: {
    type: Boolean,
    default: false
  },

  // Trending rank (1-10). Rendered as a quiet tabular numeral left of the
  // photo. Only the Trending tab passes it — the numeral means "trending
  // rank" and appears nowhere else.
  rank: {
    type: Number,
    default: null
  },

  // Keep the rank gutter (empty) on unnumbered rows of a ranked list so
  // thumbnails stay on one vertical line past row 10. Trending only.
  rankGutter: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['open-choose-member-dialog'])
// Subtle profile-view count (matches the restaurant profile treatment)
const profileViews = computed(() => props.item.profileViewCount ?? props.item.raw?.profileViewCount ?? 0)
const restaurantsStore = useRestaurantsStore()
const { restaurants, viewingDetailsRestaurant, viewingDetailsRestaurantTab, favoriteNotes } = storeToRefs(restaurantsStore)

const reservationsStore = useReservationsStore()

const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)

const isLoggedIn = computed(() => myUser.value && myUser.value.userLevel !== 'dummy')

const userGroupsStore = useUserGroupsStore()

// Google Places service instance
const googlePlacesService = new GooglePlacesService()

// State for on-demand fetched Google photo URL
const fetchedGooglePhotoUrl = ref(null)
const isFetchingGooglePhoto = ref(false)
const hasFetchedGooglePhoto = ref(false)
const imageLoadFailed = ref(false)

// Helper to get Google Places photo URL
const getGooglePhotoUrl = (photoReference, maxWidth = 200, maxHeight = 200) => {
  const apiKey = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY
  
  return `https://d128sk95sboo22.cloudfront.net/maps/api/place/photo?maxwidth=${maxWidth}&maxheight=${maxHeight}&photo_reference=${photoReference}&key=${apiKey}`
}

// Check if we have local images (uploaded or event spaces)
const hasLocalImages = computed(() => {
  const imageIds = props.item.imageIds ?? props.item.raw?.imageIds
  if (imageIds && imageIds.length > 0) return true

  const eventSpaces = props.item.eventSpaces ?? props.item.raw?.eventSpaces
  if (eventSpaces?.length > 0) {
    if (eventSpaces.some(space => space.imageId)) return true
  }

  return false
})

// Check if we have cached Google Places photos
const hasCachedGooglePhotos = computed(() => {
  const googlePlaceDetails = props.item.googlePlaceDetails ?? props.item.raw?.googlePlaceDetails
  
  return googlePlaceDetails?.photos?.length > 0 && googlePlaceDetails.photos[0].photo_reference
})

// Check if we can fetch Google Places photos (has placeId but no cached photos)
const canFetchGooglePhotos = computed(() => {
  const googlePlaceId = props.item.googlePlaceId ?? props.item.raw?.googlePlaceId
  
  return googlePlaceId && !hasLocalImages.value && !hasCachedGooglePhotos.value
})

// Check if restaurant has an image (with fallback to event spaces, then Google Places)
const hasRestaurantImage = computed(() => {
  if (hasLocalImages.value) return true
  if (hasCachedGooglePhotos.value) return true
  if (fetchedGooglePhotoUrl.value) return true

  // Show image container if we can fetch (will show loading state)
  if (canFetchGooglePhotos.value) return true
  
  return false
})

// Get restaurant image URL
// Priority: 1) uploaded images, 2) event spaces images, 3) cached Google Places, 4) fetched Google Places
const getRestaurantImageUrl = computed(() => {
  const imageIds = props.item.imageIds ?? props.item.raw?.imageIds

  // Use uploaded images from database
  if (imageIds?.length) {
    return mediaUrl(imageIds[0], 'thumb')
  }

  // Fallback to event spaces images
  const eventSpaces = props.item.eventSpaces ?? props.item.raw?.eventSpaces
  if (eventSpaces?.length > 0) {
    const spaceWithImage = eventSpaces.find(space => space.imageId)
    if (spaceWithImage) {
      return mediaUrl(spaceWithImage.imageId, 'thumb')
    }
  }

  // Fallback to on-demand fetched Google Places photo (takes priority if cached failed)
  if (fetchedGooglePhotoUrl.value) {
    return fetchedGooglePhotoUrl.value
  }

  // Fallback to cached Google Places photos (skip if previously failed)
  if (!imageLoadFailed.value) {
    const googlePlaceDetails = props.item.googlePlaceDetails ?? props.item.raw?.googlePlaceDetails
    if (googlePlaceDetails?.photos?.length > 0) {
      const photoRef = googlePlaceDetails.photos[0].photo_reference
      if (photoRef) {
        return getGooglePhotoUrl(photoRef)
      }
    }
  }

  return null
})

// Fetch Google Places photo on-demand
const fetchGooglePlacePhoto = async ({ force = false } = {}) => {
  if (isFetchingGooglePhoto.value || hasFetchedGooglePhoto.value) return
  if (!force && !canFetchGooglePhotos.value) return

  const googlePlaceId = props.item.googlePlaceId ?? props.item.raw?.googlePlaceId
  if (!googlePlaceId) return

  isFetchingGooglePhoto.value = true
  try {
    const placeDetails = await googlePlacesService.getPlaceDetails(googlePlaceId, ['photos'])
    if (placeDetails?.photos?.length > 0) {
      const photoRef = placeDetails.photos[0].photo_reference
      if (photoRef) {
        fetchedGooglePhotoUrl.value = getGooglePhotoUrl(photoRef)
      }
    }
  } catch (error) {
    console.error('Failed to fetch Google Places photo:', error)
  } finally {
    isFetchingGooglePhoto.value = false
    hasFetchedGooglePhoto.value = true
  }
}

// Handle image load failure - attempt fresh Google Places fetch
const onImageError = () => {
  const googlePlaceId = props.item.googlePlaceId ?? props.item.raw?.googlePlaceId
  if (googlePlaceId && !hasFetchedGooglePhoto.value) {
    imageLoadFailed.value = true
    fetchGooglePlacePhoto({ force: true })
  }
}

// Lazy loading state - only load images when they're in viewport
const imageRef = ref(null)
const shouldLoadImage = ref(false)

// Determine if image should load immediately (above the fold) or lazily
const isAboveTheFold = computed(() => props.index < 4) // First 4 images load immediately

// Intersection Observer instance
let observer = null

// Use Intersection Observer for lazy loading
onMounted(async () => {
  // Load immediately if above the fold
  if (isAboveTheFold.value) {
    shouldLoadImage.value = true

    // Fetch Google Places photo if needed
    if (canFetchGooglePhotos.value) {
      fetchGooglePlacePhoto()
    }
    
    return
  }

  // Wait for DOM to be ready
  await nextTick()

  // Otherwise, use Intersection Observer
  if (imageRef.value && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            shouldLoadImage.value = true

            // Fetch Google Places photo if needed
            if (canFetchGooglePhotos.value) {
              fetchGooglePlacePhoto()
            }
            if (observer) {
              observer.disconnect()
              observer = null
            }
          }
        })
      },
      {
        rootMargin: '50px' // Start loading 50px before image enters viewport
      }
    )
    observer.observe(imageRef.value)
  } else {
    // Fallback: load all images if IntersectionObserver not supported
    shouldLoadImage.value = true

    // Fetch Google Places photo if needed
    if (canFetchGooglePhotos.value) {
      fetchGooglePlacePhoto()
    }
  }
})

onUnmounted(() => {
  // Cleanup observer
  if (observer) {
    observer.disconnect()
    observer = null
  }
})

const showRestaurantDetails = restaurant => {
  if (restaurant) {
    viewingDetailsRestaurantTab.value = "Details"
    viewingDetailsRestaurant.value = restaurant
  }
}

// Helper function to preload user profile image
const preloadProfileImage = profileImageId => {
  return new Promise((resolve, reject) => {
    if (!profileImageId) {
      resolve()
      
      return
    }

    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => resolve() // Resolve even on error to not block the UI
    img.src = mediaUrl(profileImageId, 'small')

    // Timeout after 3 seconds to prevent indefinite waiting
    setTimeout(() => resolve(), 3000)
  })
}

const newDraft = async (market, restaurant) => {
  try {
    await usersStore.showLoadingOverlay()

    if (!myUser.value) {
      await usersStore.loginNewDummyUser()
    }
    await usersStore.startNewReservation({ restaurant, market })
    await nextTick()
    const currentUser = myUser.value
    if (currentUser?.profileImageId) {
      await preloadProfileImage(currentUser.profileImageId)
    }
  } catch (error) {
    console.error('Error creating new draft:', error)
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}

// Title block ref (drives the heart: fans drawer)
const titleBlockRef = ref(null)

// Note dialog
const isNoteDialogOpen = ref(false)
const noteText = ref('')
const savingNote = ref(false)

const restaurantId = computed(() => {
  const raw = props.item.raw ?? props.item
  
  return raw?._id || raw?.id
})

const existingNote = computed(() => {
  const id = restaurantId.value
  
  return id ? favoriteNotes.value[id] : null
})

const hasNote = computed(() => !!existingNote.value?.note)

// The card sits under the restaurant's name, so the note does not repeat it,
// and more than one note collapses to a count (Kapil, 2026-09-18).
const cardRestaurantName = computed(() => (props.item.raw ?? props.item)?.name || '')
const {
  visibleNotes: cardNotes, hiddenCount: cardHiddenNotes, expanded: cardNotesExpanded,
  loading: cardNotesLoading, toggle: toggleCardNotes, moreLabel: cardMoreLabel,
} = useRestaurantNotes(restaurantId, cardRestaurantName)

const favorited = computed(() => {
  if (myUser.value?._id) {
    // Check the prop item directly
    const restaurant = props.item.raw ?? props.item
    if (restaurant?.unfavorited) return false
    if (restaurant?.isFavorited) return true

    // Also look up from the store (which gets updated on favorite/unfavorite)
    const rid = restaurantId.value
    if (rid) {
      const storeRestaurant = restaurants.value.find(r => r._id === rid || r.id === rid)
      const favoritedBy = storeRestaurant?.favoritedBy ?? restaurant?.favoritedBy
      if (favoritedBy && Array.isArray(favoritedBy)) {
        if (favoritedBy.some(id => id?.toString() === myUser.value._id?.toString())) return true
      }
      if (myUser.value.favoritedRestaurants?.includes(rid)) return true
    }
  }
  
  return false
})

const openNoteDialog = () => {
  noteText.value = existingNote.value?.note || ''
  isNoteDialogOpen.value = true
}

const noteError = ref('')

const saveNote = async () => {
  savingNote.value = true
  noteError.value = ''

  const res = await restaurantsStore.saveFavoriteNote(restaurantId.value, noteText.value)

  savingNote.value = false
  if (res && res.ok === false) {
    noteError.value = res.message

    return
  }
  isNoteDialogOpen.value = false
}

// Show booking date/time for just booked items
const showBookingDateTime = computed(() => {
  return props.item.date && (props.item.confirmedTime || props.item.preferredTime)
})

// Get occasion for just booked items
// For instant bookings or reservations on Feb 14, show "Valentine's Day"
const bookingOccasion = computed(() => {
  const reservationType = props.item.reservationType ?? props.item.raw?.reservationType
  if (reservationType === 'instant') {
    const experienceName = props.item.experienceName ?? props.item.raw?.experienceName
    if (experienceName) return experienceName
  }

  return props.item.occasion ?? props.item.raw?.occasion ?? null
})

const bookingDate = computed(() => {
  if (!showBookingDateTime.value) return ''
  const time = props.item.confirmedTime || props.item.preferredTime
  
  return moment(props.item.date + ' ' + time, 'YYYY-MM-DD hh:mm:a').format("ddd")
})

const bookingTime = computed(() => {
  if (!showBookingDateTime.value) return ''
  const time = props.item.confirmedTime || props.item.preferredTime
  
  return moment(props.item.date + ' ' + time, 'YYYY-MM-DD hh:mm:a').format("h:mma")
})

// Calculate time since booking was accepted
const timeSinceBooked = computed(() => {
  const acceptedAt = props.item.acceptedAt ?? props.item.raw?.acceptedAt
  if (!acceptedAt) return ''

  const now = moment()
  const bookedTime = moment(acceptedAt)
  const diffMinutes = now.diff(bookedTime, 'minutes')

  if (diffMinutes < 60) {
    return `${diffMinutes}m`
  }

  const diffHours = now.diff(bookedTime, 'hours')
  if (diffHours < 24) {
    return `${diffHours}h`
  }

  const diffDays = now.diff(bookedTime, 'days')
  if (diffDays <= 30) {
    return `${diffDays}d`
  }

  const diffMonths = now.diff(bookedTime, 'months')
  if (diffMonths <= 11) {
    return `${diffMonths}mo`
  }

  const diffYears = now.diff(bookedTime, 'years')

  return `${diffYears}yr`
})

const bookedText = computed(() => {
  const bookingCount = props.item.bookingCount ?? props.item.raw?.bookingCount
  const lastBookingPeriod = props.item.lastBookingPeriod ?? props.item.raw?.lastBookingPeriod
  if (!bookingCount || !lastBookingPeriod || lastBookingPeriod === 'never') return ''

  let timeString = lastBookingPeriod
  if (bookingCount >= 3 && lastBookingPeriod !== 'last 1yr+') {
    timeString = `incl ${lastBookingPeriod}`
  }

  if (bookingCount >= 100) return `100+ times, ${timeString}`
  if (bookingCount >= 50) return `50+ times, ${timeString}`
  if (bookingCount >= 20) return `20+ times, ${timeString}`
  if (bookingCount >= 10) return `10+ times, ${timeString}`
  if (bookingCount >= 3) return `3+ times, ${timeString}`
  
  return `in ${timeString}`
})

// Friends who favorited - prefer prop (from friends-favorites endpoint), else use store map
const friendsForChips = computed(() => {
  if (props.friendsWhoFavoritedProp) return props.friendsWhoFavoritedProp
  
  return null // FriendsLikedChips will use store map via restaurantId
})

// Open the "Fans of" drawer via the title block's heart
const openFansDrawer = () => {
  titleBlockRef.value?.openFavoritedBy()
}

// Compact-row expand: extra data (booking info, views, notes) reveals via
// the down-caret at the row's right edge, keeping collapsed cards a
// consistent height. The row body itself always opens the profile.
const isExpanded = ref(false)

// Notes (view/edit/add) live in the expanded section for favorited restaurants
const showNoteSection = computed(() => isLoggedIn.value && favorited.value)

const hasExpandedContent = computed(() =>
  profileViews.value > 0 || !!bookedText.value ||
  showBookingDateTime.value || !!timeSinceBooked.value ||
  (showNoteSection.value && !props.inlineNotes)
)

const onRowClick = () => {
  showRestaurantDetails(props.item.raw ?? props.item)
}
</script>

<template>
  <div class="card-grid-item cursor-pointer">
    <div
      class="card-wrapper-new"
      :class="{ 'card-wrapper-new--guest': !isLoggedIn }"
    >
      <!--
        Trending rank — quiet tabular numeral, top-10 Trending only.
        Unnumbered rows of a ranked list keep the empty gutter so the
        photo column stays aligned. 
      -->
      <div
        v-if="rank || rankGutter"
        class="rank-numeral"
      >
        {{ rank || ' ' }}
      </div>

      <!-- Thumbnail (Left) — top-aligned to the restaurant name -->
      <div
        ref="imageRef"
        class="thumb-left"
        @click="showRestaurantDetails(item.raw ?? item)"
      >
        <div class="position-relative thumb-left-inner">
          <!-- Show image when we have a URL and should load -->
          <VImg
            v-if="(shouldLoadImage || isAboveTheFold) && getRestaurantImageUrl"
            :src="getRestaurantImageUrl"
            class="h-100 restaurant-image"
            cover
            gradient="to left bottom, rgba(69, 181, 169, 0.1), rgba(2, 2, 2, 0.3)"
            @error="onImageError"
          >
            <template #placeholder>
              <VRow
                class="fill-height ma-0"
                align="center"
                justify="center"
              >
                <VProgressCircular
                  indeterminate
                  color="grey-lighten-5"
                />
              </VRow>
            </template>
            <template #error>
              <div class="image-monogram-placeholder">
                <span class="thumb-initial">{{ ((item.name ?? item.raw?.name) || 'R').charAt(0) }}</span>
              </div>
            </template>
          </VImg>
          <!-- Branded placeholder: RM monogram tile (consistent across cards) -->
          <div
            v-else
            class="image-monogram-placeholder"
          >
            <span class="thumb-initial">{{ ((item.name ?? item.raw?.name) || 'R').charAt(0) }}</span>
          </div>
        </div>
      </div>


      <!--
        Content (Middle): row click expands extra info when present,
        otherwise opens the profile; the name always opens the profile 
      -->
      <div
        class="card-content-left"
        @click="onRowClick"
      >
        <!--
          Text column: name row, then group, then the Preferred word.
          No icon rail — Preferred is a plain tappable word and group/
          private-dining detail lives on the restaurant profile.
        -->
        <div class="main-content-container">
          <!--
            THE shared identity cluster — name, heart + count, group,
            Preferred — one component on every restaurant row
          -->
          <RestaurantTitleBlock
            ref="titleBlockRef"
            :restaurant="item.raw ?? item"
            :name="item.name ?? item.raw?.name"
            :market="item.market ?? item.raw?.market"
            :group="(item.group ?? item.raw?.group) || ''"
            :status="(item.status ?? item.raw?.status) || ''"
            @name-click="showRestaurantDetails(item.raw ?? item)"
          />

          <!-- Subtitle Content (below the shared cluster) -->
          <div class="sub-content-container">
            <p
              v-if="!hideMarket"
              class="content-subtitle text-truncate"
            >
              {{ item.market ?? item.raw?.market }}
            </p>
            <!-- Member Preferred status badge -->
            <div
              v-if="memberPreferred"
              class="member-preferred-badge"
              :aria-label="$t('restaurant.preferred.badge')"
            >
              <VIcon
                icon="mdi-star-check"
                size="11"
                style="margin-right: 3px; vertical-align: middle;"
              />
              <CmsText
                k="restaurant.preferred.badge"
                tag="span"
              />
            </div>

            <!--
              Favorited by [avatars] +N — own line below name/group.
              Logged-in rows reserve this line's height even when no one
              in the network favorited, so every member row matches; the
              slot doesn't exist logged-out (no empty gap for guests).
            -->
            <div
              v-if="isLoggedIn"
              class="favby-slot"
            >
              <FriendsLikedChips
                :restaurant-id="restaurantId"
                :friends="friendsForChips"
                :label="favoritedByLabel"
                @click="openFansDrawer"
              />
            </div>

            <!-- Inline note (Favorites): shown on the card like Bookings -->
            <template v-if="inlineNotes && showNoteSection">
              <p
                v-if="hasNote"
                class="content-subtitle card-expanded-line card-expanded-note card-note-inline"
                @click.stop="openNoteDialog"
              >
                <VIcon
                  icon="mdi-note-text"
                  size="13"
                  color="primary-dark"
                />
                {{ cardNotes[0]?.note }}
              </p>
              <p
                v-else
                class="content-subtitle card-expanded-line card-expanded-action card-note-inline"
                @click.stop="openNoteDialog"
              >
                <VIcon
                  icon="mdi-note-text-outline"
                  size="13"
                />
                Add note
              </p>
              <p
                v-for="(entry, i) in cardNotes.slice(1)"
                :key="entry.entryId || i"
                class="content-subtitle card-expanded-line card-expanded-note card-note-stacked"
                @click.stop="openNoteDialog"
              >
                {{ entry.note }}
              </p>
              <button
                v-if="hasNote && cardHiddenNotes > 0"
                type="button"
                class="card-notes-toggle"
                :aria-expanded="cardNotesExpanded"
                @click.stop="toggleCardNotes"
              >
                {{ cardMoreLabel }}
                <VIcon
                  :icon="cardNotesExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  size="14"
                />
              </button>
            </template>
          </div>
        </div>

        <!-- Expanded details: views + booking info (full-width row via flex wrap) -->
        <VExpandTransition>
          <div
            v-if="isExpanded && hasExpandedContent"
            class="card-expanded"
          >
            <p
              v-if="bookedText"
              class="content-subtitle card-expanded-line"
            >
              <VIcon
                icon="mdi-check"
                size="14"
              />
              Booked {{ bookedText }}
            </p>
            <p
              v-if="showBookingDateTime"
              class="content-subtitle card-expanded-line"
            >
              {{ bookingDate }} |
              <VIcon
                icon="mdi-account-multiple-outline"
                size="12"
              />
              {{ item.partySize ?? item.raw?.partySize }} |
              {{ bookingTime }} <span v-if="bookingOccasion">| {{ bookingOccasion }}</span>
            </p>
            <p
              v-if="timeSinceBooked"
              class="content-subtitle card-expanded-line"
            >
              Booked {{ timeSinceBooked }} ago
            </p>
            <!-- Views: one quiet bottom-stat line, same slot on every tab -->
            <p
              v-if="profileViews > 0"
              class="content-subtitle card-expanded-line card-expanded-views"
            >
              <VIcon
                icon="mdi-poll"
                size="12"
              />
              {{ profileViews.toLocaleString() }} views
            </p>
            <!--
              Member's note: below the stats; full text wraps.
              Tap the note (or "Add note") to open the editor 
            -->
            <template v-if="showNoteSection && !inlineNotes">
              <p
                v-if="hasNote"
                class="content-subtitle card-expanded-line card-expanded-note"
                @click.stop="openNoteDialog"
              >
                <VIcon
                  icon="mdi-note-text"
                  size="13"
                  color="primary-dark"
                />
                {{ cardNotes[0]?.note }}
              </p>
              <p
                v-else
                class="content-subtitle card-expanded-line card-expanded-action"
                @click.stop="openNoteDialog"
              >
                <VIcon
                  icon="mdi-note-text-outline"
                  size="13"
                />
                Add note
              </p>
              <p
                v-for="(entry, i) in cardNotes.slice(1)"
                :key="entry.entryId || i"
                class="content-subtitle card-expanded-line card-expanded-note card-note-stacked"
                @click.stop="openNoteDialog"
              >
                {{ entry.note }}
              </p>
              <button
                v-if="hasNote && cardHiddenNotes > 0"
                type="button"
                class="card-notes-toggle"
                :aria-expanded="cardNotesExpanded"
                @click.stop="toggleCardNotes"
              >
                {{ cardMoreLabel }}
                <VIcon
                  :icon="cardNotesExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  size="14"
                />
              </button>
            </template>
          </div>
        </VExpandTransition>
      </div>


      <!--
        Right-edge controls: quiet borderless caret (expands the drawer in
        place) paired with the outlined "+" (opens the request flow) — the
        "+" is the only bordered control. The caret keeps its slot when a
        row has nothing to expand so the "+" aligns down the list.
      -->
      <div class="row-actions">
        <button
          class="expand-caret"
          :class="{
            'expand-caret--open': isExpanded,
            'expand-caret--reserve': !hasExpandedContent,
          }"
          :aria-label="isExpanded ? 'Collapse details' : 'Expand details'"
          :tabindex="hasExpandedContent ? 0 : -1"
          @click.stop="isExpanded = !isExpanded"
        >
          <VIcon
            icon="mdi-chevron-down"
            size="18"
          />
        </button>
        <button
          class="add-btn-right"
          aria-label="Add to request"
          @click.stop="newDraft(item.market ?? item.raw?.market, item.name ?? item.raw?.name)"
        >
          +
        </button>
      </div>
    </div>

    <!-- Note Editor Dialog -->
    <VDialog
      v-model="isNoteDialogOpen"
      max-width="420"
      @click:outside="isNoteDialogOpen = false"
    >
      <VCard class="note-dialog-card">
        <div class="note-dialog-header">
          <VIcon
            icon="mdi-note-text-outline"
            size="20"
            color="primary"
          />
          <span class="note-dialog-title">Store notes for {{ item.name ?? item.raw?.name }}</span>
        </div>
        <VCardText class="note-dialog-body">
          <VTextarea
            v-model="noteText"
            placeholder="Favorite dishes, tables, team members, occasions, who recommended..."
            :counter="NOTE_MAX_LENGTH"
            :maxlength="NOTE_MAX_LENGTH"
            rows="3"
            auto-grow
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="note-textarea"
          />
          <p
            v-if="noteError"
            class="text-error text-caption mt-1 mb-0"
          >
            {{ noteError }}
          </p>
        </VCardText>
        <VCardActions class="note-dialog-actions">
          <VSpacer />
          <VBtn
            variant="text"
            size="small"
            @click="isNoteDialogOpen = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            variant="flat"
            size="small"
            rounded="lg"
            :loading="savingNote"
            @click="saveNote"
          >
            Save Note
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
.card-grid-item {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  cursor: pointer;
  height: 100%; /* fill the grid row so paired columns align */
}

.card-wrapper-new {
  // Line-separated row, not a box: thin hairline between rows, no card
  // border/shadow — more air. Thumbnail top-aligns to the restaurant name.
  // Metrics follow the RM Home Redesign prototype: 13px rows / 12px gaps
  // on mobile, 18px rows / 16px gaps on desktop, one scale for both auth
  // states (logged-in rows grow naturally via the Favorited-by line).
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 13px 2px;
  border-bottom: 1px solid var(--ds-border-subtle);
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  height: 100%; /* row hairlines land at the same y in both columns */
}

@media (min-width: 960px) {
  .card-wrapper-new {
    gap: 16px;
    padding-block: 18px;
  }
}

// Trending rank — small muted tabular figure (DM Sans, no circle/badge),
// top-aligned with the restaurant name line
.rank-numeral {
  flex: 0 0 18px;
  margin-top: 0; /* top-aligned to the name's first line */
  font-family: var(--ds-font-body);
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-muted);
  font-variant-numeric: tabular-nums;
  line-height: 1.25;
}

@media (min-width: 960px) {
  .rank-numeral {
    flex-basis: 24px;
    margin-top: 1px; /* text top level with the 23px name */
    font-size: 14px;
  }
}

// Right-edge controls: caret + "+" paired ~8px apart
.row-actions {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

// Quiet borderless expand control — same chevron shape as before, now a
// 30px circular hit target beside the "+" (enough tap separation at 390px)
.expand-caret {
  position: relative;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--ds-text-disabled);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0; /* top-aligned to the name line */
  transition: color 0.2s ease, background-color 0.2s ease;

  // 44px minimum tap target without changing the visual size
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 44px;
    height: 44px;
    transform: translate(-50%, -50%);
  }

  .v-icon {
    transition: transform 0.2s ease;
  }

  &:hover {
    color: var(--ds-text-body);
    background: var(--ds-surface-alt);
  }

  // Keeps its slot (and the "+" column aligned) when there is nothing
  // to expand
  &--reserve {
    visibility: hidden;
    pointer-events: none;
  }
}

.expand-caret--open .v-icon {
  transform: rotate(180deg);
}

// Request action — quiet outlined circle, the only bordered control
.add-btn-right {
  position: relative;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--ds-surface-card);
  border: 1.5px solid var(--ds-border-strong);
  color: var(--ds-brand-primary-dark);
  font-size: 17px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0; /* top-aligned to the name line */
  transition: border-color 0.2s ease, transform 0.2s ease;

  // 44px minimum tap target without changing the visual size
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 44px;
    height: 44px;
    transform: translate(-50%, -50%);
  }

  &:hover {
    border-color: var(--ds-brand-primary-dark);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
}

.card-content-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden; // Prevent overflow
  cursor: pointer; // Required for Safari mobile touch events
}

.card-expanded {
  // The drawer opens in place, aligned to the text column; no divider line —
  // just spacing between "Favorited by" and the views/note below.
  min-width: 0;
  padding: 8px 2px 0 0;
  text-align: left;
}

.card-expanded-line {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--ds-text-muted);
}

.card-expanded-action {
  cursor: pointer;
  // Real tap target on mobile — the label, not just an icon
  padding: 4px 0;

  &:hover {
    color: var(--ds-brand-primary-dark);
  }
}

// Quiet bottom-stat: views are context, not a competing headline number
.card-expanded-views {
  font-size: 11px;
  color: var(--ds-text-muted);
}

// Vertical rhythm, scoped under .card-expanded so it outranks
// .content-subtitle's margin reset: 6px between kin lines, 12px before each
// group break (views, note, Add note) — identical on every tab
.card-expanded .card-expanded-line {
  margin-top: 6px;

  &:first-child {
    margin-top: 0;
  }
}

.card-expanded .card-expanded-views,
.card-expanded .card-expanded-action,
.card-expanded .card-expanded-note {
  margin-top: 12px;

  &:first-child {
    margin-top: 0;
  }
}

// Inline note on the card (Favorites) — a little air above, same wrap rules
.card-note-inline {
  margin-top: 6px;
}

// Doubled selector so the full note always wraps: .content-subtitle (defined
// later) sets nowrap/ellipsis and would otherwise win the specificity tie
.card-expanded-line.card-expanded-note {
  align-items: flex-start;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
  cursor: pointer;

  .v-icon {
    flex-shrink: 0;
    margin-top: 1px;
  }

  &:hover {
    color: var(--ds-brand-primary-dark);
  }
}


/* Text column — plain stacked lines (no icon rail anymore) */
.main-content-container {
  min-width: 0;
}

/* Name + heart/count geometry comes from the global .rm-title-heart
   spec (styles.scss) — one source for every restaurant row */

.member-preferred-badge {
  display: inline-flex;
  align-items: center;
  background: var(--ds-brand-primary-dark);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 2px 7px;
  border-radius: 12px;
  margin-top: 3px;
  line-height: 1.4;
  white-space: nowrap;
}

// Reserved Favorited-by line (logged-in only) — matches the chips row
// height so member rows align whether or not anyone favorited
.favby-slot {
  min-height: 20px;
}

.time-since-booked {
  font-family: var(--ds-font-body);
  font-size: 11px;
  font-weight: 400;
  color: var(--ds-text-muted);
  margin-left: 4px;
  white-space: nowrap;
}

.sub-content-container {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.booking-datetime {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.booking-occasion {
  display: flex;
  align-items: center;
  gap: 4px;
}



.content-subtitle {
  font-family: var(--ds-font-body);
  font-size: 13.5px; // comfortable, per the master type scale
  font-weight: 400;
  line-height: 1.3;
  color: var(--ds-text-body); // readable ink, not washed-out gray
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  // Hospitality group — quiet subtitle under the name (no parens);
  // readable ink per the master type scale, not light gray
  &--muted {
    color: var(--ds-text-body);
  }
}

/* Thumbnail — prototype scale: 44px mobile, 56px desktop */
.thumb-left {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: var(--ds-radius-md);
  overflow: hidden;
  cursor: pointer; // Required for Safari mobile touch events
}

@media (min-width: 960px) {
  .thumb-left {
    width: 56px;
    height: 56px;
  }
}

.thumb-left-inner {
  width: 100%;
  height: 100%;
}

.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

// Fallback tile: the restaurant's initial in the serif on a soft tile —
// identical size, radius, and crop to real thumbnails
.image-monogram-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ds-radius-md);
  background: var(--ds-brand-primary-light);
}

.thumb-initial {
  font-family: var(--ds-font-heading);
  font-weight: var(--ds-font-medium);
  font-size: 15px;
  color: color-mix(in srgb, var(--ds-brand-primary-dark) 55%, var(--ds-surface-card));
}

@media (min-width: 960px) {
  .thumb-initial {
    font-size: 19px;
  }
}

// Desktop — prototype scale (name/group/Preferred scale lives in
// RestaurantTitleBlock)
@media (min-width: 960px) {
  .content-subtitle {
    font-size: 14px;
  }
}
/* Notes on a card: the count is a real target, the rest open in place
   (Kapil, 2026-09-18) */
.card-notes-toggle {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-height: 32px;
  padding: 0 4px 0 18px;
  border: 0;
  background: none;
  font-size: 13px;
  color: var(--ds-text-link, #0F7B6C);
  cursor: pointer;
}

.card-notes-toggle:hover,
.card-notes-toggle:focus-visible {
  text-decoration: underline;
}

/* a second or third note aligns under the first, past the icon */
.card-note-stacked {
  padding-left: 18px;
}
</style>